import React, { useState, useContext } from 'react';
import { Box, Container, Fade, Paper, Typography, useTheme, SxProps, Theme, Button, Stack } from '@mui/material';
import { backgroundImageStyles } from '../../styles/backgroundImageStyles';
import AuthHeroImage from '../common/AuthHeroImage';
import LanguageToggler from '../common/LanguageToggler';
import { useLang } from '../../contexts/langContext';
import LicenseInfoDialog from './LicenseDialog';
import { useBranding } from '../../contexts/BrandingContext';
import { VersionDisplay } from '../common/VersionDisplay';

interface AuthLayoutProps {
  children: React.ReactNode;
  title: string;
}

function AuthLayout({ children, title }: AuthLayoutProps) {
  const theme = useTheme();
  const { lang } = useLang();
  const branding = useBranding();
  const currentYear = new Date().getFullYear();
  const [licenseDialogOpen, setLicenseDialogOpen] = useState(false);

  const styles = backgroundImageStyles(theme) as SxProps<Theme>;

  const productName = branding.productName?.[lang as 'en' | 'zh-CN'] ?? branding.productName?.['en'] ?? 'Default Product';
  const copyright = branding.copyright?.[lang as 'en' | 'zh-CN'] ?? branding.copyright?.['en'] ?? 'Default Copyright';

  return (
    <Box sx={styles}>
      <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
        <LanguageToggler />
      </Box>
      <Container maxWidth="sm" sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper
          elevation={24} 
          sx={{ 
            p: { xs: 2.5, sm: 4 },
            width: '100%',
            maxWidth: 360,
            borderRadius: 3,
            [theme.breakpoints.down('sm')]: {
              maxWidth: '100%',
              height: '100vh',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              borderRadius: 0,
              p: 2.5,
            },
          }}
        >
          <AuthHeroImage />

          <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ my: 1.5 }}>
            <Typography
              variant="h4" 
              align="center"
              sx={{
                fontWeight: 700,
                background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.light})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {productName}
            </Typography>
            <Typography 
              variant="h6" 
              color="secondary"
              sx={{
                opacity: 0.7,
                fontWeight: 400
              }}
            >
              {title}
            </Typography>
          </Stack>
          {children}
          <Typography 
            variant="body2" 
            color="text.secondary" 
            align="center" 
            sx={{ 
              mt: 3,
              fontSize: '0.75rem',
              whiteSpace: 'pre-line',
            }}
          >
            &copy; {currentYear} {copyright}
          </Typography>
        </Paper>
      </Container>
      {/* Bottom-right: version + license */}
      <Box
        sx={theme => ({
          position: 'absolute',
          bottom: 16,
          right: 16,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 0.75,
          color: theme.palette.common.white,
          textShadow: '0 1px 2px rgba(0,0,0,0.4)',
        })}
      >
        <VersionDisplay sx={{ color: 'inherit', opacity: 0.9, fontWeight: 500 }} />

        <Button
          onClick={() => setLicenseDialogOpen(true)}
          size="small"
          aria-label="license information"
          sx={theme => ({
            background: `${theme.palette.background.paper}30`,
            border: theme.palette.divider,
            px: 2,
            py: 1,
          })}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {lang === 'en' ? 'License Info' : '许可证信息'}
            </Typography>
          </Box>
        </Button>
        <LicenseInfoDialog isOpen={licenseDialogOpen} onClose={() => setLicenseDialogOpen(false)} />
      </Box>
    </Box>
  );
}

export default AuthLayout; 